import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { getFAQs } from '../firebase/database';
import SEO from '../components/common/SEO';
import { Spinner } from '../components/common/Loader';
import ContactCTA from '../components/common/ContactCTA';

const PolicyPage = ({ title, children }) => (
  <>
    <SEO title={title}/>
    <div className="bg-[#0f2347] py-14">
      <div className="max-w-4xl mx-auto px-4">
        <h1 className="font-display text-4xl font-black text-white">{title}</h1>
        <p className="text-white/60 text-sm mt-2">SitSmart · info@sitsmart.store</p>
      </div>
    </div>
    <div className="max-w-4xl mx-auto px-4 py-14">
      <div className="space-y-6 text-gray-700 text-sm leading-relaxed">{children}</div>
    </div>
  </>
);

// ─── About ────────────────────────────────────────────────────────────────────
export const AboutPage = () => (
  <>
    <SEO title="About Us" description="Learn about SitSmart - Pakistan's premier smart seating brand."/>
    <div className="bg-[#0f2347] py-20 relative overflow-hidden">
      <div className="absolute inset-0 diagonal-bg opacity-30"/>
      <div className="relative max-w-4xl mx-auto px-4 text-center">
        <motion.div initial={{opacity:0,y:30}} animate={{opacity:1,y:0}}>
          <p className="text-[#e63946] text-sm font-bold uppercase tracking-widest mb-3">Our Story</p>
          <h1 className="font-display text-5xl font-black text-white mb-4">About SitSmart</h1>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">Pakistan's #1 smart seating brand, crafting comfort and style for every home since 2014.</p>
        </motion.div>
      </div>
    </div>

    {/* Who We Are */}
    <section className="py-20">
      <div className="max-w-6xl mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
          <motion.div initial={{opacity:0,x:-30}} whileInView={{opacity:1,x:0}} viewport={{once:true}}>
            <p className="text-[#e63946] text-sm font-bold uppercase tracking-widest mb-3">Who We Are</p>
            <h2 className="font-display text-4xl font-black text-gray-900 mb-6">Smart Seating For Every Pakistani Home</h2>
            <p className="text-gray-600 leading-relaxed mb-4">SitSmart was born from a simple vision: every Pakistani family deserves quality seating that's durable, affordable, and stylish. Since 2014, we've been delivering premium chairs and tables to homes, offices, and businesses across Pakistan.</p>
            <p className="text-gray-600 leading-relaxed">From our durable plastic chairs to ergonomic office solutions and safe baby chairs, every product is crafted with care and quality assurance.</p>
          </motion.div>
          <motion.div initial={{opacity:0,x:30}} whileInView={{opacity:1,x:0}} viewport={{once:true}}>
            <img src="https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=700&q=80" alt="About SitSmart"
              className="w-full shadow-luxury" onError={e=>e.target.src='https://via.placeholder.com/700x500?text=SitSmart'}/>
          </motion.div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-20">
          {[{num:'50K+',label:'Happy Customers'},{num:'200+',label:'Products'},{num:'10+',label:'Years Experience'},{num:'100+',label:'Cities Delivered'}].map(({num,label},i)=>(
            <motion.div key={label} initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{delay:i*0.1}}
              className="text-center p-8 bg-[#0f2347]">
              <div className="text-4xl font-black text-[#e63946] mb-2">{num}</div>
              <div className="text-white/70 text-sm uppercase tracking-wider">{label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* Vision, Mission, Values */}
    <section className="py-20 bg-[#f8f9fc]">
      <div className="max-w-6xl mx-auto px-4">
        <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} className="text-center mb-14">
          <p className="text-[#e63946] text-sm font-bold uppercase tracking-widest mb-3">What Drives Us</p>
          <h2 className="font-display text-4xl font-black text-gray-900">Vision, Mission & Values</h2>
        </motion.div>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              title: 'Our Vision',
              icon: <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z M15 12a3 3 0 11-6 0 3 3 0 016 0z" />,
              text: "To become Pakistan's most trusted name in smart seating — present in every home, office, and business across the country, recognized for quality that lasts and prices that make sense."
            },
            {
              title: 'Our Mission',
              icon: <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />,
              text: 'To design and deliver durable, comfortable, and affordable furniture for every Pakistani household — backed by honest service, fast delivery, and a team that genuinely cares about getting it right.'
            },
            {
              title: 'Our Values',
              icon: <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />,
              text: 'Quality without compromise, honesty in every interaction, and respect for every customer — whether you\'re buying one chair or fitting out an entire office.'
            },
          ].map(({ title, icon, text }, i) => (
            <motion.div key={title} initial={{opacity:0,y:30}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{delay:i*0.12}}
              className="bg-white p-8 shadow-card hover:shadow-hover transition-all duration-300">
              <div className="w-14 h-14 bg-[#1a3a6e]/10 rounded-2xl flex items-center justify-center mb-5">
                <svg className="w-7 h-7 text-[#1a3a6e]" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">{icon}</svg>
              </div>
              <h3 className="font-display text-xl font-black text-gray-900 mb-3">{title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* Gallery */}
    <section className="py-20">
      <div className="max-w-6xl mx-auto px-4">
        <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} className="text-center mb-14">
          <p className="text-[#e63946] text-sm font-bold uppercase tracking-widest mb-3">Behind The Brand</p>
          <h2 className="font-display text-4xl font-black text-gray-900">A Look Inside SitSmart</h2>
        </motion.div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=400&q=80',
            'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&q=80',
            'https://images.unsplash.com/photo-1449247709967-d4461a6a6103?w=400&q=80',
            'https://images.unsplash.com/photo-1464146072230-91cabc968266?w=400&q=80',
          ].map((src, i) => (
            <motion.div key={src} initial={{opacity:0,scale:0.92}} whileInView={{opacity:1,scale:1}} viewport={{once:true}} transition={{delay:i*0.08}}
              className="aspect-square overflow-hidden group">
              <img src={src} alt="SitSmart" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                onError={e=>e.target.src='https://via.placeholder.com/400?text=SitSmart'}/>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    <ContactCTA />
  </>
);

// ─── Contact ──────────────────────────────────────────────────────────────────
export const ContactPage = () => {
  const [form, setForm] = useState({name:'',email:'',phone:'',subject:'',message:''});
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    await new Promise(r=>setTimeout(r,1500));
    setSent(true); setLoading(false);
  };

  return (
    <>
      <SEO title="Contact Us"/>
      <div className="bg-[#0f2347] py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-[#e63946] text-sm font-bold uppercase tracking-widest mb-3">Get In Touch</p>
          <h1 className="font-display text-4xl font-black text-white">Contact Us</h1>
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-4 py-16">
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="space-y-5">
            {[
              {title:'Call Us',info:'+92 333 1420200',sub:'Mon-Sat, 9am-7pm',href:'tel:+923331420200'},
              {title:'WhatsApp',info:'+92 333 1420200',sub:'Quick response',href:'https://wa.me/923331420200'},
              {title:'Email Us',info:'info@sitsmart.store',sub:'Reply within 24 hours',href:'mailto:info@sitsmart.store'},
              {title:'Visit Us',info:'Karachi, Sindh',sub:'Pakistan'},
            ].map(({title,info,sub,href})=>(
              <div key={title} className="flex gap-4 p-5 bg-white shadow-card hover:shadow-hover transition-all group">
                <div className="w-12 h-12 bg-[#1a3a6e]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#1a3a6e] transition-colors rounded-xl">
                  <svg className="w-5 h-5 text-[#1a3a6e] group-hover:text-white transition-colors" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                  </svg>
                </div>
                <div>
                  <p className="font-bold text-gray-900 text-sm">{title}</p>
                  {href ? <a href={href} className="text-gray-700 text-sm hover:text-[#1a3a6e]">{info}</a> : <p className="text-gray-700 text-sm">{info}</p>}
                  <p className="text-gray-400 text-xs">{sub}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="lg:col-span-2 bg-white shadow-card p-8">
            {sent?(
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7"/></svg>
                </div>
                <h3 className="font-display text-2xl font-bold mb-2">Message Sent!</h3>
                <p className="text-gray-500">We'll get back to you within 24 hours.</p>
                <button onClick={()=>setSent(false)} className="btn-primary mt-6 text-sm">Send Another</button>
              </div>
            ):(
              <form onSubmit={handleSubmit} className="space-y-4">
                <h2 className="font-display text-2xl font-bold text-gray-900 mb-6">Send a Message</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><label className="block text-xs font-bold text-gray-700 mb-1.5">Full Name *</label><input required className="input-field" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))} placeholder="Your name"/></div>
                  <div><label className="block text-xs font-bold text-gray-700 mb-1.5">Email *</label><input type="email" required className="input-field" value={form.email} onChange={e=>setForm(f=>({...f,email:e.target.value}))} placeholder="email@example.com"/></div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><label className="block text-xs font-bold text-gray-700 mb-1.5">Phone</label><input className="input-field" value={form.phone} onChange={e=>setForm(f=>({...f,phone:e.target.value}))} placeholder="+92 300 0000000"/></div>
                  <div><label className="block text-xs font-bold text-gray-700 mb-1.5">Subject *</label>
                    <select required className="input-field" value={form.subject} onChange={e=>setForm(f=>({...f,subject:e.target.value}))}>
                      <option value="">Select subject</option>
                      {['Order Inquiry','Product Question','Bulk Order','Other'].map(s=><option key={s}>{s}</option>)}
                    </select></div>
                </div>
                <div><label className="block text-xs font-bold text-gray-700 mb-1.5">Message *</label>
                  <textarea required rows={5} className="input-field resize-none" value={form.message} onChange={e=>setForm(f=>({...f,message:e.target.value}))} placeholder="How can we help you?"/></div>
                <button type="submit" disabled={loading} className="btn-primary flex items-center gap-2 text-sm">
                  {loading?<Spinner size="sm" color="white"/>:<svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/></svg>}
                  {loading?'Sending...':'Send Message'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

// ─── Shipping Policy ──────────────────────────────────────────────────────────
export const ShippingPolicyPage = () => (
  <PolicyPage title="Shipping Policy">
    <div className="bg-amber-50 border-2 border-amber-300 p-5 rounded-xl mb-6">
      <p className="font-black text-amber-800 text-base mb-1">⚠️ Important — Check Before You Pay</p>
      <p className="text-amber-700 font-semibold">First CHECK the parcel, THEN pay the rider. No breakage or any claim will be accepted after payment is made to the rider.</p>
    </div>
    <h2 className="font-display text-2xl font-bold text-gray-900 mb-3">Delivery Information</h2>
    <p>At SitSmart, we strive to deliver your orders as quickly and safely as possible across Pakistan.</p>
    <h3 className="font-bold text-gray-900 text-base mt-5 mb-2">Delivery Timeframes</h3>
    <p className="bg-blue-50 border border-blue-200 p-4 rounded-lg font-medium text-blue-800">
      Delivery time depends on your city and product availability. There is no specific fixed delivery time — it varies by location and order. Our team will update you via WhatsApp/call with estimated delivery.
    </p>
    <ul className="space-y-2 list-disc pl-5 mt-3">
      <li>Major cities (Karachi, Lahore, Islamabad): Usually faster</li>
      <li>Other cities: Delivery time varies</li>
      <li>All deliveries subject to courier availability in your area</li>
    </ul>
    <h3 className="font-bold text-gray-900 text-base mt-5 mb-2">Shipping Charges</h3>
    <p>Shipping charges vary based on your city and order quantity. Our team will confirm the exact shipping cost when your order is confirmed. Charges are calculated at checkout based on our current rate structure.</p>
    <h3 className="font-bold text-gray-900 text-base mt-5 mb-2">Cash on Delivery (COD)</h3>
    <p>COD is available in select cities and for limited quantities only. For large orders (3+ items) or cities outside Karachi, advance payment is required via JazzCash, EasyPaisa, or bank transfer.</p>
    <h3 className="font-bold text-gray-900 text-base mt-5 mb-2">Order Processing</h3>
    <p>Orders are processed within 1-2 business days. You will receive a WhatsApp/SMS notification once your order is dispatched.</p>
    <h3 className="font-bold text-gray-900 text-base mt-5 mb-2">Delivery Coverage</h3>
    <p>We deliver to all major cities across Pakistan. Contact us for delivery availability in remote areas.</p>
  </PolicyPage>
);

// ─── Privacy Policy ───────────────────────────────────────────────────────────
export const PrivacyPolicyPage = () => (
  <PolicyPage title="Privacy Policy">
    <p>SitSmart is committed to protecting your privacy. This policy explains how we collect, use, and safeguard your personal information.</p>
    <h3 className="font-bold text-gray-900 text-base mt-5 mb-2">Information We Collect</h3>
    <p>We collect name, email, phone number, shipping address, and payment information when you place an order or create an account.</p>
    <h3 className="font-bold text-gray-900 text-base mt-5 mb-2">How We Use Your Information</h3>
    <ul className="space-y-2 list-disc pl-5">
      <li>To process and fulfill your orders</li>
      <li>To send order confirmations and delivery updates via WhatsApp/SMS</li>
      <li>To improve our products and services</li>
      <li>To send promotional messages (you can opt-out anytime)</li>
    </ul>
    <h3 className="font-bold text-gray-900 text-base mt-5 mb-2">Data Security</h3>
    <p>We use industry-standard security measures to protect your data. We never sell your personal information to third parties.</p>
    <h3 className="font-bold text-gray-900 text-base mt-5 mb-2">Contact</h3>
    <p>For privacy concerns, contact us at <a href="mailto:info@sitsmart.store" className="text-[#1a3a6e] hover:underline">info@sitsmart.store</a></p>
  </PolicyPage>
);

// ─── Terms ────────────────────────────────────────────────────────────────────
export const TermsPage = () => (
  <PolicyPage title="Terms & Conditions">
    <p>By using SitSmart's website and placing orders, you agree to these terms. Please read them carefully.</p>
    <h3 className="font-bold text-gray-900 text-base mt-5 mb-2">Orders & Payment</h3>
    <p>All orders are subject to availability and confirmation. We reserve the right to refuse or cancel any order. Prices are in Pakistani Rupees (PKR) and subject to change without notice.</p>
    <h3 className="font-bold text-gray-900 text-base mt-5 mb-2">Cash on Delivery</h3>
    <p>COD is available only in select cities and for limited quantities. For 3+ items or cities outside Karachi, advance payment is required. We reserve the right to change COD availability at any time.</p>
    <h3 className="font-bold text-gray-900 text-base mt-5 mb-2">Parcel Inspection</h3>
    <p className="font-semibold">Check the parcel BEFORE paying the rider. SitSmart is not responsible for any damage claims made after payment to the delivery rider.</p>
    <h3 className="font-bold text-gray-900 text-base mt-5 mb-2">No Returns</h3>
    <p>All sales are final. We do not accept returns. Please review your order carefully before purchasing.</p>
    <h3 className="font-bold text-gray-900 text-base mt-5 mb-2">Intellectual Property</h3>
    <p>All content on this website, including images, text, and logos, is the property of SitSmart and protected by copyright laws.</p>
    <h3 className="font-bold text-gray-900 text-base mt-5 mb-2">Contact</h3>
    <p>For any questions, contact us at <a href="mailto:info@sitsmart.store" className="text-[#1a3a6e] hover:underline">info@sitsmart.store</a> or WhatsApp <a href="https://wa.me/923331420200" className="text-[#1a3a6e] hover:underline">+92 333 1420200</a>.</p>
  </PolicyPage>
);
